import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  NgZone,
  OnDestroy,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EmployeeProfileEntity, SearchEmployeesUseCase } from '@features/employee-360';
import { GetTenantComponentsStateUseCase } from '@features/tenant-components';
import {
  NeogovTopNavbarElement,
  NeoTopNavbarProfileMenuItem,
  NeoTopNavbarSearchResultItem,
} from '@neogov/top-navbar-definition';
import {
  NeoTopNavbarAnchorClickableHelper,
  NeoTopNavbarService,
} from '@neogov/unified-suite-library/services';
import { BrowserNavigation, LoggerProvider } from '@shared';
import { catchError, debounceTime, filter, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ClearAuthorizationUseCase } from 'src/app/auth/application/use-cases/clear-authorization/clear-authorization.use-case';
import { AuthState } from '../../../auth/application/states/auth.state';
import { CaseAuthorizationState } from '../../../auth/application/states/case-authorization.state';
import { InvestigationAuthorizationState } from '../../../auth/application/states/investigation-authorization.state';
import { CasePermission } from '../../../auth/domain/enums/case-permission.enum';
import { InvestigationPermission } from '../../../auth/domain/enums/investigation-permission.enum';
import { AppConfigService } from '../../config/app-config.service';
import { DialogProvider } from '../dialog/dialog.provider';
import { DialogSize } from '../dialog/dialog.types';
import { EIDCProvider } from '../oidc/eidc.provider';
import { ProfileImageState } from '../profile-image/profile-image.state';
import { InvestigationsNotificationsNavbarService } from './investigations-notifications-navbar.service';
import { NavBarNavigationItem, NavItemSource } from './models';
import { NotificationsNavbarService } from './notifications-navbar.service';

type NavigationMode = 'cases' | 'investigations' | 'employee';

/**
 * NavBar Provider
 *
 * Manages the top navigation bar initialization, configuration, and state.
 * Handles all navbar features including logo, navigation items, app switcher,
 * search, help, settings, and user profile.
 *
 * **Key Features:**
 * - Signal-based reactive state management for navigation items
 * - Automatic route tracking and navigation item highlighting
 * - Dynamic navigation item addition/removal
 * - Permission-based navigation filtering
 * - Employee search with autocomplete
 * - User profile management with avatar
 *
 * **Architecture:**
 * - Uses Angular signals for reactive state (`_currentNavigationSources`)
 * - Computed transformation from NavItemSource to NavBarNavigationItem
 * - Automatic cleanup on component/page destruction
 * - Centralized transformation logic for navigation items
 *
 * This provider is global (providedIn: 'root') and can be injected throughout the application.
 *
 * @example
 * ```typescript
 * // Add a dynamic navigation item
 * const source: NavItemSource = {
 *   id: 'new-item',
 *   title: 'New Item',
 *   anchorHref: '/path',
 * };
 * navBarProvider.addNavigationItem(source);
 *
 * // Remove when done
 * navBarProvider.removeNavigationItem('new-item');
 * ```
 */
@Injectable({ providedIn: 'root' })
export class NavBarProvider implements OnDestroy {
  private readonly _router = inject(Router);
  private readonly _injector = inject(Injector);
  private readonly _authState = inject(AuthState);
  private readonly _clearAuthorization = inject(ClearAuthorizationUseCase);
  private readonly _configService = inject(AppConfigService);
  private readonly _eidcProvider = inject(EIDCProvider);
  private readonly _neoTopNavbarService = inject(NeoTopNavbarService);
  private readonly _notificationsNavbarService = inject(NotificationsNavbarService);
  private readonly _investigationsNotificationsNavbarService = inject(
    InvestigationsNotificationsNavbarService,
  );
  private readonly _browserNavigation = inject(BrowserNavigation);
  private readonly _dialog = inject(DialogProvider);
  private readonly _searchEmployees = inject(SearchEmployeesUseCase);
  private readonly _logger = inject(LoggerProvider).createScoped('NavBar');
  private readonly _tenantComponentsState = inject(GetTenantComponentsStateUseCase).execute();
  private readonly _caseAuthorizationState = inject(CaseAuthorizationState);
  private readonly _investigationAuthorizationState = inject(InvestigationAuthorizationState);
  private readonly _destroy$ = new Subject<void>();
  private readonly _searchTerm$ = new Subject<string>();

  private _searchSubscribed = false;
  private _lastSearchTerm = '';
  private _navbarInterval: ReturnType<typeof setInterval> | null = null;
  private _navbar: NeogovTopNavbarElement | null = null;
  private _productNameValue = 'HR Cases';
  private readonly _profileImageState = inject(ProfileImageState);

  /**
   * Signal holding the raw navigation item sources.
   * This is the single source of truth for navigation state.
   */
  private readonly _currentNavigationSources = signal<NavItemSource[]>([]);

  /**
   * Computed signal that transforms navigation sources into renderable items.
   * Automatically recomputes when _currentNavigationSources changes.
   */
  private readonly _currentNavigationItems = computed(() =>
    this._buildNavItems(this._currentNavigationSources()),
  );

  /**
   * Navigation loading state signal (private writable).
   */
  private readonly _isLoadingNavigation = signal(false);

  /**
   * Public readonly accessor for navigation loading state.
   */
  public readonly isLoadingNavigation = this._isLoadingNavigation.asReadonly();

  /**
   * True once the user has triggered logout. Drives the full-page "Closing session" overlay while
   * the OIDC sign-out redirect (or local redirect) is in flight, so the app doesn't flash stale
   * content during teardown.
   */
  private readonly _closingSession = signal(false);
  public readonly closingSession = this._closingSession.asReadonly();

  /**
   * Initializes the navbar by waiting for the navbar element to be available in the DOM.
   * Uses an interval to check for the navbar element and configures it once found.
   * Automatically hides search bar if starting on Employee Workspace route.
   *
   * @param ngZone - Angular's NgZone for proper change detection
   */
  public initialize(ngZone: NgZone): void {
    this._navbarInterval = setInterval(() => {
      const navbar = document.querySelector('ce-neogov-top-navbar');
      if (navbar && this._navbarInterval) {
        clearInterval(this._navbarInterval);
        this._navbar = navbar as NeogovTopNavbarElement;
        this._injectNavbarStyles(navbar);
        this.configureNavbar(this._navbar, ngZone);
        this._configureNeoLogo();
        // Only show search bar if not on Employee Workspace route
        // Use BrowserNavigation to get real browser URL (router.url not ready during app init)
        const currentPath = this._browserNavigation.getPathname();
        const isEmployeeWorkspace = currentPath.startsWith('/employee');
        if (isEmployeeWorkspace) {
          this.hideSearchBar();
        } else {
          this._configureSearch();
        }
        if (!isEmployeeWorkspace) {
          this._configureHelp();
          this._configureSettings();
        }
        this._configureAppSwitcher();
        this._configureLegalLinks();
        this._notificationsNavbarService.initialize(this._navbar);
        // Initialize AFTER the Cases bell service: inside /investigations the Cases service disables
        // the bell (out of its area) and this service re-enables it with Investigations data; the
        // ordering ensures Investigations wins in its own area without clobbering Cases in /cases.
        this._investigationsNotificationsNavbarService.initialize(this._navbar);

        // Track route changes to update selected navigation
        this._trackRouteChanges();

        // Watch for user state changes - this handles initial setup and subsequent changes
        runInInjectionContext(this._injector, () => {
          effect(() => {
            const user = this._authState.user();
            if (user && this._navbar) {
              this.setupUserProfile();
            }
          });

          // Rebuild the profile menu when product roles or portal flags change so the switcher stays in sync.
          effect(() => {
            this._caseAuthorizationState.role();
            this._investigationAuthorizationState.role();
            this._caseAuthorizationState.permissions();
            this._investigationAuthorizationState.permissions();
            this._tenantComponentsState.sharedServicesPortalEnabled();
            this._tenantComponentsState.employeeRelationsPortalEnabled();
            if (this._navbar && this._authState.user()) {
              this.setupUserProfile();
              // Permissions can load after the navbar mounts, so re-evaluate the Settings gear
              // once they arrive (or change) rather than only on the initial configure. The
              // employee workspace hides Settings entirely (handled at initialize), so skip it here.
              if (this._getCurrentNavigationMode() !== 'employee') {
                this._configureSettings();
              }
            }
          });

          // Re-apply the navbar avatar whenever the shared profile-image state
          // publishes a new URL (initial load, upload refresh, etc.).
          effect(() => {
            const photoUrl = this._profileImageState.imageUrl();
            if (this._navbar && this._authState.user()) {
              this._applyProfileToNavbar(photoUrl);
            }
          });
        });

        // Rebuild profile menu on navigation so My Profile visibility reflects current route
        this._router.events
          .pipe(
            filter((e) => e instanceof NavigationEnd),
            takeUntil(this._destroy$),
          )
          .subscribe(() => {
            if (this._navbar && this._authState.user()) {
              this.setupUserProfile();
            }
          });
      }
    }, 200);
  }

  private _injectNavbarStyles(navbar: Element): void {
    const shadowRoot = navbar.shadowRoot;
    if (!shadowRoot) return;
    const style = document.createElement('style');
    style.textContent = `
      .neo-top-navbar__left-container { min-width: 320px; }
      .product-name { max-width: none !important; overflow: visible !important; text-overflow: unset !important; white-space: nowrap !important; }
    `;
    shadowRoot.appendChild(style);
  }

  /**
   * Configures the navbar element with navigation items and settings.
   *
   * @param navbar - The navbar element to configure
   * @param ngZone - Angular's NgZone for change detection
   */
  public configureNavbar(navbar: NeogovTopNavbarElement, ngZone: NgZone): void {
    navbar.ngZone = ngZone;
    navbar.productName = this._productNameValue;
    navbar.navigation.setNavigation({
      isEnabled: true,
      items: [],
    });
  }

  /**
   * Configures the NeoGov logo with a link to the home page.
   * Also disables logo autoload to prevent fetching from accounts API without proper config.
   */
  private _configureNeoLogo(): void {
    if (!this._navbar) return;

    this._navbar.neoLogo = {
      type: 'anchor',
      anchorHref: '/',
      onClick: (e: Event) => {
        if (NeoTopNavbarAnchorClickableHelper.allowEventToGoThrough(e)) {
          return { preventOverlayClose: true };
        }
        this._router.navigate(['/']);
        return { preventOverlayClose: false };
      },
    };
  }

  /**
   * Configures the app switcher using the unified suite library service.
   * Makes an API call to fetch available apps for the current user.
   * Skipped in local environment since there's no OIDC token for authentication.
   */
  private _configureAppSwitcher(): void {
    if (!this._navbar) return;

    // Skip app switcher in local environment - no OIDC token available
    if (this._configService.isLocal()) {
      this._logger.info('App switcher skipped in local environment');
      return;
    }

    const appSwitcherBaseUrl = this._configService.appSwitcherBaseUrl;
    if (!appSwitcherBaseUrl) {
      this._logger.info('App switcher base URL not configured, skipping');
      return;
    }

    // Subscribe to app switcher config changes from the service
    this._neoTopNavbarService.appSwitcherConfig$
      .pipe(filter((config) => !!config))
      .subscribe((config) => {
        if (this._navbar) {
          this._navbar.appSwitcher.setAppSwitcher(config);
        }
      });

    // Trigger the app switcher setup
    this._neoTopNavbarService.setupAppSwitcher(
      appSwitcherBaseUrl,
      this._configService.productCode,
      true,
    );
  }

  /**
   * Configures the global employee search bar.
   * Wires the navbar's native autocomplete results to the employee search use
   * case; selecting an employee opens the Employee 360 View modal.
   */
  private _configureSearch(): void {
    if (!this._navbar) return;

    this._subscribeToSearchTerm();

    this._navbar.search.setSearch({
      isEnabled: true,
      isAutocompleteEnabled: true,
      shouldFocusOnInitialization: false,
      onInputValueChange: (value: string) => {
        this._searchTerm$.next(value);
      },
      onInputEnterKeyPress: () => false,
    });
  }

  /**
   * Subscribes to debounced search-term input and pushes the matching
   * employees into the navbar's native search-results dropdown.
   * Guarded so the subscription is only set up once.
   */
  private _subscribeToSearchTerm(): void {
    if (this._searchSubscribed) return;
    this._searchSubscribed = true;

    this._searchTerm$
      .pipe(
        debounceTime(250),
        switchMap((term) =>
          this._searchEmployees.execute({ term }).pipe(
            map((result) => ({ term, employees: result.employees })),
            catchError(() => of({ term, employees: [] as EmployeeProfileEntity[] })),
          ),
        ),
        takeUntil(this._destroy$),
      )
      .subscribe(({ term, employees }) => {
        this._lastSearchTerm = term;
        this._renderSearchResults(employees);
      });
  }

  /**
   * Maps employee results to the navbar's search-result config and applies it.
   */
  private _renderSearchResults(employees: EmployeeProfileEntity[]): void {
    if (!this._navbar) return;

    const items: NeoTopNavbarSearchResultItem[] = employees.map((employee) => ({
      title: employee.fullName,
      neoAvatarConfig: {
        firstName: employee.firstName,
        lastName: employee.lastName,
      },
      descriptionGroups: [
        {
          descriptionItems: [employee.email, { label: employee.department, color: 'informative' }],
        },
      ],
      clickable: {
        type: 'anchor',
        anchorHref: '#',
        anchorAriaLabel: `Open 360 view for ${employee.fullName}`,
        anchorAutId: `employee-search-result-${employee.userId}`,
        onClick: (e: Event) => {
          e.preventDefault();
          this._openEmployee360(employee.userId);
          return { preventOverlayClose: false };
        },
      },
    }));

    this._navbar.search.setSearchResults({
      searchTerm: this._lastSearchTerm,
      searchResultGroups: [
        {
          label: '',
          searchResultItems: items,
          searchResultTotalCount: items.length,
        },
      ],
      searchResultViewAllConfig: {
        isEnabled: false,
        clickable: {
          type: 'anchor',
          anchorHref: '#',
          onClick: () => ({}),
        },
      },
    });
  }

  /**
   * Opens the Employee 360 View modal for the given employee.
   * The modal component is imported lazily to avoid a static core to
   * presentation dependency.
   */
  private _openEmployee360(userId: string): Promise<void> {
    return import('@presentation/components/employee-360-modal').then(
      ({ Employee360ModalComponent }) => {
        this._dialog.open(Employee360ModalComponent, {
          data: { userId },
          size: DialogSize.lg,
          panelClass: 'employee-360-dialog',
        });
      },
    );
  }

  /**
   * Hides the search bar from the navbar.
   * Use when specific pages don't need the search functionality.
   *
   * @example
   * ```typescript
   * ngOnInit() {
   *   this.navBarProvider.hideSearchBar();
   * }
   * ```
   */
  public hideSearchBar(): void {
    if (!this._navbar) return;

    this._navbar.search.setSearch({
      isEnabled: false,
      isAutocompleteEnabled: false,
      shouldFocusOnInitialization: false,
      onInputValueChange: () => {
        // No-op
      },
      onInputEnterKeyPress: () => false,
    });
  }

  /**
   * Shows the search bar in the navbar.
   * Use to restore search bar visibility after hiding it.
   *
   * @example
   * ```typescript
   * ngOnDestroy() {
   *   this.navBarProvider.showSearchBar();
   * }
   * ```
   */
  public showSearchBar(): void {
    this._configureSearch();
  }

  public hideHelpAndSettings(): void {
    if (!this._navbar) return;
    this._navbar.help.setHelp({ isEnabled: false, items: [] });
    this._navbar.settings.setSettings({ isEnabled: false, items: [] });
  }

  public showHelpAndSettings(): void {
    this._configureHelp();
    this._configureSettings();
  }

  /**
   * Configures the help icon with a link to the help center.
   */
  private _configureHelp(): void {
    if (!this._navbar) return;

    const helpUrl = this._configService.helpUrl;

    this._navbar.help.setHelp({
      isEnabled: true,
      items: [
        {
          title: 'Help',
          id: 'help',
          clickable: {
            type: 'anchor',
            anchorHref: helpUrl,
            anchorTarget: '_blank',
            onClick: () => ({}),
          },
        },
      ],
    });
  }

  /**
   * Configures the settings gear icon in the navbar.
   */
  private _configureSettings(): void {
    if (!this._navbar) return;

    // Settings is gated by the same "access settings" permission the /admin route guards check
    // (cm.access_settings). Roles without it — e.g. Agents — must not see the gear at all.
    if (!this._canAccessSettings()) {
      this._navbar.settings.setSettings({ isEnabled: false, items: [] });
      return;
    }

    this._navbar.settings.setSettings({
      isEnabled: true,
      items: [
        {
          title: 'Settings',
          id: 'settings',
          clickable: {
            type: 'button',
            onClick: () => {
              this.setSelectedNavigationId('');
              this._router.navigate([this._getSettingsRoutePath()]);
              return {};
            },
          },
        },
      ],
    });
  }

  /**
   * Whether the current user may open Admin Settings, based on the active portal's
   * "access settings" permission. Investigations settings follow the investigation
   * permission; every other context (including Cases) uses the case permission.
   */
  private _canAccessSettings(): boolean {
    if (this._getCurrentNavigationMode() === 'investigations') {
      return this._investigationAuthorizationState.hasPermission(
        InvestigationPermission.CaseManagementAccessSettings,
      );
    }
    return this._caseAuthorizationState.hasPermission(CasePermission.CaseManagementAccessSettings);
  }

  /**
   * Configures the legal links (Terms & Privacy) in the profile dropdown.
   */
  private _configureLegalLinks(): void {
    if (!this._navbar) return;

    this._navbar.legalLinks.setLegalLinks({
      isEnabled: true,
    });
  }

  /**
   * Builds NavBarNavigationItem array from NavItemSource array.
   * Transforms raw navigation sources into configured navigation items with click handlers.
   *
   * @param sources - Array of navigation item sources
   * @returns Array of configured navigation items
   */
  private _buildNavItems(sources: NavItemSource[]): NavBarNavigationItem[] {
    return sources.map((source) =>
      NavBarNavigationItem.createFromNavItemSource(source, this._router, this),
    );
  }

  /**
   * Tracks route changes and updates the selected navigation item.
   * Automatically marks the navigation item that matches the current route as active.
   * Called on initialization and on every route change.
   */
  private _trackRouteChanges(): void {
    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._updateSelectedNavigation();
        this._updateProductName();
        this._syncNavbarForNavigationMode();
      });

    // Set initial selected navigation
    this._updateSelectedNavigation();
    this._updateProductName();
  }

  /**
   * Shows or hides search, help, and settings based on the active portal.
   * Called on every route change so transitions between Employee Workspace
   * and Cases/Investigations correctly restore (or remove) those controls.
   */
  private _syncNavbarForNavigationMode(): void {
    const isEmployee = this._getCurrentNavigationMode() === 'employee';
    if (isEmployee) {
      this.hideSearchBar();
      this.hideHelpAndSettings();
    } else {
      this.showSearchBar();
      this.showHelpAndSettings();
    }
  }

  /**
   * Product name shown in the navbar per portal: "HR Cases" for the Cases
   * portal, "Employee Relations" for the Investigations portal.
   */
  private _getProductNameForMode(mode: NavigationMode | null): string {
    switch (mode) {
      case 'investigations':
        return 'Employee Relations';
      case 'employee':
        return 'Employee Portal';
      case 'cases':
      default:
        return 'HR Shared Services';
    }
  }

  /**
   * Syncs the navbar product name with the active portal so switching between
   * HR Cases and Employee Relations updates the title.
   */
  private _updateProductName(): void {
    this.setProductName(this._getProductNameForMode(this._getCurrentNavigationMode()));
  }

  /**
   * Updates the selected navigation item based on the current route.
   * Finds the navigation source whose anchorHref matches the current path
   * and marks it as selected in the navbar.
   */
  private _updateSelectedNavigation(): void {
    const currentPath = this._router.url;
    const sources = this._currentNavigationSources();

    // Find the navigation source that matches the current path
    // Check for exact match first, then partial match
    const matchedSource = sources.find((source) => {
      // Exact match
      if (currentPath === source.anchorHref) {
        return true;
      }
      // Partial match - current path starts with the navigation href
      // e.g., '/cases/dashboard' matches '/cases'
      if (
        currentPath.startsWith(source.anchorHref + '/') ||
        currentPath.startsWith(source.anchorHref + '?')
      ) {
        return true;
      }
      return false;
    });

    if (matchedSource && this._navbar) {
      this.setSelectedNavigationId(matchedSource.id);
    }
  }

  /**
   * Updates the navigation items dynamically from NavItemSource array.
   * This method can be used to change navigation items at runtime.
   *
   * @param sources - Array of navigation item sources
   */
  public updateNavigation(sources: NavItemSource[]): void {
    this._currentNavigationSources.set(sources);
    const items = this._currentNavigationItems();
    if (this._navbar) {
      this._navbar.navigation.setNavigation({
        isEnabled: true,
        items,
      });
    }
  }

  /**
   * Adds a new navigation item source at a specified index.
   * Useful for injecting navigation items dynamically from specific pages.
   *
   * @param source - The navigation item source to add
   * @param index - The index position where the item should be inserted (defaults to last position)
   * @returns True if the item was added successfully, false otherwise
   *
   * @example
   * ```typescript
   * const navSource: NavItemSource = {
   *   id: 'cases-new-case',
   *   title: 'New Case',
   *   anchorHref: '/cases/create',
   * };
   * this.navBarProvider.addNavigationItem(navSource);
   * ```
   */
  public addNavigationItem(source: NavItemSource, index?: number): boolean {
    if (!this._navbar) {
      return false;
    }

    const currentSources = this._currentNavigationSources();

    // Check if item with same id already exists
    const existingIndex = currentSources.findIndex((navSource) => navSource.id === source.id);
    if (existingIndex !== -1) {
      // Item already exists, don't add it again
      return false;
    }

    // Insert at specified index or at the end
    const insertIndex =
      index !== undefined
        ? Math.min(Math.max(0, index), currentSources.length)
        : currentSources.length;
    const updatedSources = [
      ...currentSources.slice(0, insertIndex),
      source,
      ...currentSources.slice(insertIndex),
    ];

    this._currentNavigationSources.set(updatedSources);
    const items = this._currentNavigationItems();
    this._navbar.navigation.setNavigation({
      isEnabled: true,
      items,
    });

    return true;
  }

  public hasNavigationItem(itemId: string): boolean {
    return this._currentNavigationSources().some((s) => s.id === itemId);
  }

  /**
   * Removes a navigation item by its ID.
   *
   * @param itemId - The ID of the navigation item to remove
   * @returns True if the item was removed successfully, false otherwise
   */
  public removeNavigationItem(itemId: string): boolean {
    if (!this._navbar) {
      return false;
    }

    const currentSources = this._currentNavigationSources();
    const filteredSources = currentSources.filter((source) => source.id !== itemId);

    // If no items were removed, return false
    if (filteredSources.length === currentSources.length) {
      return false;
    }

    this._currentNavigationSources.set(filteredSources);
    const items = this._currentNavigationItems();
    this._navbar.navigation.setNavigation({
      isEnabled: true,
      items,
    });

    return true;
  }

  /**
   * Sets the product name displayed in the navbar.
   *
   * @param name - Product name to display
   */
  public setProductName(name: string): void {
    this._productNameValue = name;
    if (this._navbar) {
      this._navbar.productName = name;
    }
  }

  /**
   * Sets the navigation loading state.
   * Used by navbar build use cases to indicate loading status.
   *
   * @param loading - True to show loading, false to hide
   */
  public setLoadingNavigation(loading: boolean): void {
    if (loading) {
      this._isLoadingNavigation.set(loading);
      return;
    }

    setTimeout(() => {
      this._isLoadingNavigation.set(loading);
    }, 300);
  }

  /**
   * Sets up the user profile dropdown with avatar, profile link, and logout option.
   * Matches the BackgroundSolutions user menu pattern.
   * Loads profile image from GET /api/v1/users/me/profile-image.
   */
  public setupUserProfile(): void {
    if (!this._navbar) return;

    const user = this._authState.user();
    if (!user) return;

    // Apply the profile once with the current image (may be null on first run —
    // the effect below rewrites it as soon as the shared state finishes loading).
    this._applyProfileToNavbar(this._profileImageState.imageUrl());

    // Kick the shared state to fetch. Keyed off user.id so an in-app login
    // flip forces a fresh fetch instead of inheriting the previous user's blob.
    this._profileImageState.ensureLoaded(user.id);
  }

  /**
   * Applies the current user + given photoUrl to the navbar element. The two-step
   * reset (null → real URL on the next macrotask) is a defensive against the
   * custom element sometimes ignoring updates when only `photoUrl` changes.
   */
  private _applyProfileToNavbar(photoUrl: string | null): void {
    const initialUser = this._authState.user();
    if (!this._navbar || !initialUser) return;

    this._navbar.profile.setProfile({
      isEnabled: true,
      avatar: {
        firstName: initialUser.firstName,
        lastName: initialUser.lastName,
        photoUrl: null,
      },
      profileMenuItems: this._buildProfileMenuItems(),
    });

    if (photoUrl !== null) {
      setTimeout(() => {
        // Re-read user + menu inside the callback: if auth flipped between the
        // reset above and this macrotask (rapid logout/login, user swap), we
        // must not paint the new photoUrl over the previous user's name/menu.
        const currentUser = this._authState.user();
        if (!this._navbar || !currentUser) return;
        this._navbar.profile.setProfile({
          isEnabled: true,
          avatar: {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            photoUrl,
          },
          profileMenuItems: this._buildProfileMenuItems(),
        });
      }, 0);
    }
  }

  /**
   * Refreshes the profile image in the navbar (called after upload). Kept for
   * backwards compatibility with existing callers — routes through the shared
   * state which then triggers the effect that repaints the navbar.
   */
  public refreshProfileImage(): void {
    this._profileImageState.refresh();
  }

  /**
   * Builds profile menu items matching BackgroundSolutions pattern.
   * Includes: My Profile, navigation mode switchers, Sign Out.
   */
  private _buildProfileMenuItems(): NeoTopNavbarProfileMenuItem[] {
    const menuItems: NeoTopNavbarProfileMenuItem[] = [];

    // Add Profile settings menu item only in cases/investigations context
    const profileRoute = this._resolveProfileSettingsRoute();
    if (profileRoute) {
      menuItems.push({
        label: 'Profile settings',
        clickable: {
          type: 'button' as const,
          onClick: () => {
            this.setSelectedNavigationId('');
            this._router.navigate([profileRoute]);
            return {};
          },
        },
      });
    }

    // Add navigation mode switchers based on current mode
    const currentMode = this._getCurrentNavigationMode();
    const availableTargets = this._getAvailableNavigationTargets(currentMode);

    availableTargets.forEach((targetMode) => {
      menuItems.push({
        label: this._getNavigationModeLabel(targetMode),
        clickable: {
          type: 'button' as const,
          onClick: () => {
            const path = this._getNavigationModeRoutePath(targetMode);
            this._router.navigate([path]);
            return { preventOverlayClose: false };
          },
        },
      });
    });

    // Add Sign Out menu item
    menuItems.push({
      label: 'Sign Out',
      clickable: {
        type: 'button' as const,
        onClick: () => {
          this._logout();
          return {};
        },
      },
    });

    return menuItems;
  }

  /**
   * Clears the user profile from the navbar.
   */
  public clearUserProfile(): void {
    if (!this._navbar) return;

    this._navbar.profile.setProfile({
      isEnabled: false,
      avatar: {
        firstName: '',
        lastName: '',
        photoUrl: null,
      },
      profileMenuItems: [],
    });
  }

  public setSelectedNavigationId(itemId: string): void {
    if (this._navbar) {
      this._navbar.navigation.setSelectedNavigationId(itemId);
    }
  }

  /**
   * Public entry point for logout — usable outside the navbar (e.g. error pages).
   * Delegates to the internal logout flow so all cleanup and navigation stay centralized.
   */
  public logout(): void {
    this._logout();
  }

  private _getCurrentNavigationMode(): NavigationMode | null {
    const url = this._router.url;
    if (url.startsWith('/cases')) return 'cases';
    if (url.startsWith('/investigations')) return 'investigations';
    if (url.startsWith('/employee')) return 'employee';
    return null;
  }

  /**
   * Resolves the Settings route for the active portal so the top navbar and
   * portal side menu remain visible while in Settings. Falls back to the Cases
   * portal when the current mode has no dedicated Settings area.
   */
  private _getSettingsRoutePath(): string {
    const mode = this._getCurrentNavigationMode();
    if (mode === 'investigations') return '/investigations/settings';
    return '/cases/settings';
  }

  private _getAvailableNavigationTargets(currentMode: NavigationMode | null): NavigationMode[] {
    const allModes: NavigationMode[] = ['cases', 'investigations', 'employee'];
    // Super Admin sees all portals so they can navigate to configure SKUs,
    // mirroring the portalAccessGuard and backend ProductAccessFilter bypass.
    const isSuperAdmin = this._authState.user()?.isSuperAdmin ?? false;
    return allModes.filter((mode) => {
      if (mode === currentMode) return false;
      if (isSuperAdmin) return true;
      if (mode === 'cases') {
        return (
          this._tenantComponentsState.sharedServicesPortalEnabled() &&
          this._caseAuthorizationState.role() !== null
        );
      }
      if (mode === 'investigations') {
        return (
          this._tenantComponentsState.employeeRelationsPortalEnabled() &&
          this._investigationAuthorizationState.role() !== null
        );
      }
      return true;
    });
  }

  private _getNavigationModeRoutePath(mode: NavigationMode): string {
    switch (mode) {
      case 'cases':
        return '/cases/dashboard';
      case 'investigations':
        return '/investigations/dashboard';
      case 'employee':
        return '/employee';
    }
  }

  private _getNavigationModeLabel(mode: NavigationMode): string {
    switch (mode) {
      case 'cases':
        return '⇄ Switch to HR Cases';
      case 'investigations':
        return '⇄ Switch to Employee Relations';
      case 'employee':
        return '⇄ Switch to Employee Portal';
    }
  }

  /**
   * Resolves the profile settings route based on the current URL context.
   * Returns null when not in a cases or investigations context (hides the menu item).
   */
  private _resolveProfileSettingsRoute(): string | null {
    const url = this._router.url;
    if (url === '/investigations' || url.startsWith('/investigations/'))
      return '/investigations/profile-settings/profile';
    return '/cases/profile-settings/profile';
  }

  /**
   * Logs out the user and redirects appropriately.
   * In non-local environments, performs full OIDC logout (matching BackgroundSolutions).
   * In local environment, uses browser navigation to redirect to loginPath from config.
   */
  private _logout(): void {
    this._closingSession.set(true);
    this._authState.clear();
    this._clearAuthorization.execute();
    this.clearUserProfile();
    this._profileImageState.clear();

    if (!this._configService.isLocal()) {
      this._eidcProvider.signOut();
    } else {
      this._eidcProvider.signOutLocal();
      this._browserNavigation.navigateToUrl(this._configService.loginPath);
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    // Blob URL revoke happens in ProfileImageState, which owns the URL.
  }
}
